import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, poundsFromPence } from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import { formatOrdinalDate } from "../lib/timeFormat";
import { CheckCircle2, CalendarDays, Ticket, Palette, ArrowRight, PartyPopper } from "lucide-react";

export default function SetupCompletePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/events/${id}`);
        setData(r.data);
      } catch {
        setLoadErr(true);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (loadErr) nav("/dashboard/events", { replace: true });
  }, [loadErr, nav]);

  if (!data) return <DashboardLayout title="Loading…" />;

  const event = data.event || {};
  const dates = [...(data.dates || [])].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const tickets = data.ticket_types || [];
  const slotted = !!event.is_time_slotted;
  const slotsPerDay = slotted ? ((dates[0]?.time_slots || []).length) : 0;
  const cap = dates[0]?.max_capacity || 0;

  const dateRange = dates.length
    ? (dates.length === 1
        ? formatOrdinalDate(dates[0].date, { weekday: false })
        : `${formatOrdinalDate(dates[0].date, { weekday: false })} to ${formatOrdinalDate(dates[dates.length - 1].date, { weekday: false })}`)
    : "—";

  return (
    <DashboardLayout title="Setup complete" subtitle={event.name}>
      <div className="max-w-3xl mx-auto">
        <div className="text-center st-fade-up" data-testid="setup-complete-page">
          <div className="inline-flex w-16 h-16 rounded-full bg-[#0d2818] items-center justify-center text-[#22c55e] mb-4">
            <PartyPopper size={32} />
          </div>
          <h2 className="text-3xl font-bold">Your event is set up!</h2>
          <p className="text-[#8a8a8a] mt-2">Here&apos;s a summary of what you created.</p>
        </div>

        {/* Summary card */}
        <div className="st-card p-6 mt-6" data-testid="setup-summary-card">
          <h3 className="text-lg font-bold">{event.name}</h3>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <SummaryRow icon={CalendarDays} label="Open dates"
                        value={`${dates.length} date${dates.length === 1 ? "" : "s"} added`}
                        sub={dateRange} />
            <SummaryRow icon={Ticket} label="Time slots"
                        value={slotted ? `${slotsPerDay} time slot${slotsPerDay === 1 ? "" : "s"} per day` : "Open all day"}
                        sub={slotted ? `Every ${event.slot_interval_minutes} min` : null} />
            <SummaryRow icon={Ticket} label="Ticket types"
                        value={tickets.length ? tickets.map((t) => `${t.name} — ${poundsFromPence(t.base_price_pence)}`).join(", ") : "—"} />
            <SummaryRow icon={CheckCircle2} label="Capacity"
                        value={`Up to ${cap} tickets per ${slotted ? "slot" : "date"}`} />
          </div>
        </div>

        {/* Next steps */}
        <div className="mt-8">
          <p className="text-[#dadada] font-medium">Now let&apos;s get your booking page looking great. Here&apos;s what to do next:</p>
          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            <NextCard to="/dashboard/open-days" icon={CalendarDays} title="View your open days"
                      desc="Check and manage your dates and time slots." testid="next-open-days" />
            <NextCard to="/dashboard/tickets" icon={Ticket} title="Manage your tickets"
                      desc="Adjust prices, capacity and add-ons." testid="next-tickets" />
            <NextCard to="/dashboard/branding" icon={Palette} title="Brand your booking page"
                      desc="Add your logo, colours and template." highlighted testid="next-branding" />
          </div>

          <div className="flex flex-wrap items-center gap-4 mt-6">
            <button className="st-btn" data-testid="setup-brand-cta"
                    onClick={() => nav("/dashboard/open-days?tour=true")}>
              Brand your booking page <ArrowRight size={15} />
            </button>
            <Link to="/dashboard/overview" className="st-link text-sm" data-testid="setup-goto-dashboard">
              Go to dashboard
            </Link>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function SummaryRow({ icon: Icon, label, value, sub }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-[#0d0d0d] border border-[#1a1a1a]">
      <Icon size={16} className="text-[#dc2626] mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[#7a7a7a]">{label}</div>
        <div className="text-sm text-[#dadada]">{value}</div>
        {sub && <div className="text-xs text-[#8a8a8a] mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function NextCard({ to, icon: Icon, title, desc, highlighted, testid }) {
  return (
    <Link to={to} data-testid={testid}
          className={`st-card st-card-hover p-4 block ${highlighted ? "border-[#dc2626]" : ""}`}
          style={highlighted ? { borderColor: "#dc2626" } : undefined}>
      <Icon size={18} className="text-[#dc2626]" />
      <div className="font-semibold mt-2 text-sm">{title}{highlighted && <span className="text-[10px] uppercase tracking-wider text-[#dc2626] ml-2">Recommended</span>}</div>
      <div className="text-xs text-[#8a8a8a] mt-1">{desc}</div>
    </Link>
  );
}
